import { AxiosError } from 'axios';
import { useState } from 'react';
import {
  ErrorHttpResponse,
  // HttpConfig,
  SuccessHttpResponse,
  PaginatedResponse,
} from '../../../models/https';
import { bookmarkedLocumJobsActions } from '../../../redux/slices/bookmarkedLocumJobsSlice';
import { useAppDisPatch } from '../../../redux/store';
import useHttps from '../../useHttps';

interface BookmarkJobsFilter {
  page?: string;
  perPage?: string;
}

const useGetBookmarkedLocumJobs = () => {
  const request = useHttps();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDisPatch();

  const onGetBookmarkedLocumJobsError = (
    err: AxiosError<ErrorHttpResponse>
  ) => {
    console.error(
      err?.message ?? 'Something went wrong while fetching bookmarked jobs'
    );
  };

  // Helper to wrap the callback-based request in a promise.
  const fetchPage = (
    page: number,
    perPage: number
  ): Promise<SuccessHttpResponse<PaginatedResponse>> => {
    const urlPath = `jobs-service/bookmarks?offset=${page}&limit=${perPage}`;

    // ?offset=${page}&limit=${perPage}

    return new Promise((resolve, reject) => {
      request(
        { url: urlPath, method: 'get' },
        (response: SuccessHttpResponse<PaginatedResponse>) => resolve(response),
        () => {}, // No need to update loading here.
        (error: AxiosError<ErrorHttpResponse>) => reject(error)
      );
    });
  };

  const getBookmarkedLocumJobs = async (filter?: BookmarkJobsFilter) => {
    const page = parseInt(filter?.page ?? '1', 10);
    const perPage = parseInt(filter?.perPage ?? '10', 10);

    try {
      setLoading(true);

      // Fetch the first page.
      const firstResponse = await fetchPage(page, perPage);
      // console.log('Bookmarked jobs response (page 1):', firstResponse);

      const {
        totalPages,
        totalElements,
        numberOfElements,
        first,
        last,
        content,
      } = firstResponse.data;

      let allContent: any[] = Array.isArray(content) ? content : [];

      // If more pages exist, fetch them concurrently.
      if (totalPages > 1) {
        const additionalPagePromises = [];
        // Assuming offset is a 1-indexed page number.
        for (let p = page + 1; p <= totalPages; p++) {
          additionalPagePromises.push(fetchPage(p, perPage));
        }
        const additionalResponses = await Promise.all(additionalPagePromises);
        additionalResponses.forEach((res) => {
          if (res?.data && Array.isArray(res.data.content)) {
            allContent = allContent.concat(res.data.content);
          }
        });
      }

      // Compute the complete bookmark mapping from the aggregated content.
      const bookmarkMapping = allContent.map((bookmark: any) => ({
        jobId: bookmark.job.id,
        bookmarkId: bookmark.id,
      }));

      // Dispatch the aggregated data with full bookmarkMapping.
      dispatch(
        bookmarkedLocumJobsActions.getBookmarkedLocumJobs({
          page, // original page used for UI reference
          perPage,
          totalPages,
          totalElements,
          numberOfElements,
          first,
          last,
          bookmarkJobData: allContent,
          bookmarkMapping,
        })
      );
    } catch (err) {
      onGetBookmarkedLocumJobsError(err as AxiosError<ErrorHttpResponse>);
    } finally {
      setLoading(false);
    }
  };

  return { getBookmarkedLocumJobs, loading };
};

export default useGetBookmarkedLocumJobs;
